import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Plus, Star, Sun, Calendar, Flag, Paperclip, MessageSquare, Repeat,
  ChevronRight, Check, MoreHorizontal, Trash2, Edit2, Clock, User,
  X, Send, Loader2, Sparkles, GripVertical, Users, AtSign
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent
} from '../../components/ui/dropdown-menu';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../../components/ui/popover';
import { cn } from '../../lib/utils';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTasksContext } from '../TasksLayout';
import { UserMentionInput, UserAssignSelect, UserAvatarGroup } from '../../components/tasks/UserComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = [
  { value: 'critical', label: 'Critique', color: '#EF4444', icon: '🔴' },
  { value: 'high', label: 'Haute', color: '#F59E0B', icon: '🟠' },
  { value: 'normal', label: 'Normale', color: '#3B82F6', icon: '🔵' },
  { value: 'low', label: 'Basse', color: '#6B7280', icon: '⚪' }
];

export default function TasksPage({ viewType = 'all' }) {
  const navigate = useNavigate();
  const { listId } = useParams();
  const [searchParams] = useSearchParams();
  const { refreshData, lists } = useTasksContext() || {};
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [newTaskInput, setNewTaskInput] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [currentList, setCurrentList] = useState(null);
  
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  // Determine the view title and API endpoint
  const getViewConfig = () => {
    switch (viewType) {
      case 'my-day':
        return { title: 'Ma journée', endpoint: '/api/tasks/my-day', icon: Sun, color: '#F59E0B' };
      case 'important':
        return { title: 'Important', endpoint: '/api/tasks/important', icon: Star, color: '#EF4444' };
      case 'planned':
        return { title: 'Planifié', endpoint: '/api/tasks/planned', icon: Calendar, color: '#10B981' };
      case 'assigned':
        return { title: 'Assigné à moi', endpoint: '/api/tasks/assigned-to-me', icon: User, color: '#8B5CF6' };
      case 'mentions':
        return { title: 'Mentions', endpoint: '/api/tasks/mentions', icon: AtSign, color: '#EC4899' };
      case 'list':
        return { title: currentList?.name || 'Liste', endpoint: `/api/task-lists/${listId}/tasks`, icon: null, color: currentList?.color };
      case 'search':
        return { title: `Recherche: "${searchParams.get('q')}"`, endpoint: `/api/tasks/search?q=${searchParams.get('q')}`, icon: null };
      default:
        return { title: 'Toutes les tâches', endpoint: '/api/tasks', icon: null };
    }
  };

  const viewConfig = getViewConfig();

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${viewConfig.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTasks(data.tasks || []);
      
      // Get list info if viewing a specific list
      if (viewType === 'list' && listId) {
        const listRes = await fetch(`${API_URL}/api/task-lists/${listId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const listData = await listRes.json();
        setCurrentList(listData);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [viewType, listId, searchParams.get('q')]);

  // Parse input with NLP
  const parseInput = async (input) => {
    if (!input.trim()) {
      setParsedData(null);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/tasks/parse?raw_input=${encodeURIComponent(input)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.parsed) {
        setParsedData(data.parsed);
      }
    } catch (err) {
      console.error('NLP parse error:', err);
    }
  };

  // Debounced input parsing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newTaskInput.length > 3) {
        parseInput(newTaskInput);
      } else {
        setParsedData(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newTaskInput]);

  const createTask = async () => {
    if (!newTaskInput.trim()) return;
    
    setIsCreating(true);
    try {
      const taskData = {
        title: parsedData?.title || newTaskInput,
        raw_input: newTaskInput,
        list_id: listId || null,
        priority: parsedData?.extracted_priority || 'normal',
        due_date: parsedData?.extracted_date || null,
        due_time: parsedData?.extracted_time || null,
        is_in_my_day: viewType === 'my-day',
        is_important: viewType === 'important'
      };
      
      if (parsedData?.extracted_recurrence) {
        taskData.recurrence = parsedData.extracted_recurrence;
      }
      
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(taskData)
      });
      
      if (res.ok) {
        setNewTaskInput('');
        setParsedData(null);
        fetchTasks();
        refreshData?.();
      }
    } catch (err) {
      console.error('Error creating task:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const toggleComplete = async (task, e) => {
    e?.stopPropagation();
    const endpoint = task.status === 'done' 
      ? `/api/tasks/${task.id}/uncomplete`
      : `/api/tasks/${task.id}/complete`;
    
    try {
      await fetch(`${API_URL}${endpoint}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
      refreshData?.();
    } catch (err) {
      console.error('Error toggling task:', err);
    }
  };

  const toggleImportant = async (task, e) => {
    e?.stopPropagation();
    try {
      await fetch(`${API_URL}/api/tasks/${task.id}/toggle-important`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
      refreshData?.();
    } catch (err) {
      console.error('Error toggling important:', err);
    }
  };

  const toggleMyDay = async (task, e) => {
    e?.stopPropagation();
    try {
      await fetch(`${API_URL}/api/tasks/${task.id}/toggle-my-day`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
      refreshData?.();
    } catch (err) {
      console.error('Error toggling my day:', err);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowTaskDetail(false);
      setSelectedTask(null);
      fetchTasks();
      refreshData?.();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  // Format due date with optional time from due_time field
  const formatDueDate = (dueDate, dueTime = null) => {
    if (!dueDate) return null;
    // Extract only the date part (ignore any time in due_date)
    const dateStr = dueDate.slice(0, 10);
    const date = parseISO(dateStr);
    const timeStr = dueTime ? ` ${dueTime}` : '';
    
    if (isToday(date)) return "Aujourd'hui" + timeStr;
    if (isTomorrow(date)) return 'Demain' + timeStr;
    return format(date, 'd MMM', { locale: fr }) + timeStr;
  };

  const getDueDateColor = (dueDate, status) => {
    if (!dueDate || status === 'done') return 'text-gray-500';
    const date = parseISO(dueDate);
    if (isPast(date) && !isToday(date)) return 'text-red-500';
    if (isToday(date)) return 'text-blue-500';
    return 'text-gray-500';
  };

  const Icon = viewConfig.icon;

  // Group tasks for planned view - group by DATE only, not time
  const groupedTasks = viewType === 'planned' 
    ? Object.entries(tasks.reduce((acc, task) => {
        // Extract only the date part (YYYY-MM-DD), ignore time
        const dateKey = task.due_date ? task.due_date.slice(0, 10) : 'no_date';
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(task);
        return acc;
      }, {})).sort(([a], [b]) => a.localeCompare(b))
    : null;
  
  // Format date header for grouped view (date only, no time)
  const formatDateHeader = (dateKey) => {
    if (dateKey === 'no_date') return 'Sans date';
    const date = parseISO(dateKey);
    if (isToday(date)) return "Aujourd'hui";
    if (isTomorrow(date)) return 'Demain';
    return format(date, 'EEEE d MMMM', { locale: fr });
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-7 w-7" style={{ color: viewConfig.color }} />}
          {viewType === 'list' && currentList && (
            <div 
              className="w-4 h-4 rounded"
              style={{ backgroundColor: currentList.color }}
            />
          )}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {viewConfig.title}
          </h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {tasks.length} tâche{tasks.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Quick Add */}
        <div className="mt-4 relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder="Ajouter une tâche... (ex: Réunion demain à 14h urgent)"
                value={newTaskInput}
                onChange={(e) => setNewTaskInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    createTask();
                  }
                }}
                className="pl-10 h-12 text-base"
                data-testid="task-quick-add-input"
              />
              {newTaskInput && (
                <button
                  onClick={() => { setNewTaskInput(''); setParsedData(null); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button 
              onClick={createTask} 
              disabled={!newTaskInput.trim() || isCreating}
              className="h-12 px-6"
              data-testid="task-add-btn"
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ajouter'}
            </Button>
          </div>
          
          {/* NLP Preview */}
          {parsedData && parsedData.confidence > 0.5 && (
            <div className="absolute z-10 mt-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800 w-full">
              <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                <Sparkles className="h-4 w-4" />
                <span className="font-medium">Analyse intelligente</span>
                <span className="text-xs text-blue-500">({Math.round(parsedData.confidence * 100)}% confiance)</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {parsedData.extracted_date && (
                  <Badge variant="secondary" className="bg-white dark:bg-gray-800">
                    <Calendar className="h-3 w-3 mr-1" />
                    {formatDueDate(parsedData.extracted_date)}
                  </Badge>
                )}
                {parsedData.extracted_time && (
                  <Badge variant="secondary" className="bg-white dark:bg-gray-800">
                    <Clock className="h-3 w-3 mr-1" />
                    {parsedData.extracted_time}
                  </Badge>
                )}
                {parsedData.extracted_priority && (
                  <Badge 
                    variant="secondary" 
                    className="bg-white dark:bg-gray-800"
                    style={{ color: PRIORITIES.find(p => p.value === parsedData.extracted_priority)?.color }}
                  >
                    <Flag className="h-3 w-3 mr-1" />
                    {PRIORITIES.find(p => p.value === parsedData.extracted_priority)?.label}
                  </Badge>
                )}
                {parsedData.extracted_recurrence && (
                  <Badge variant="secondary" className="bg-white dark:bg-gray-800">
                    <Repeat className="h-3 w-3 mr-1" />
                    Récurrence
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Task List */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 dark:text-gray-500 mb-2">
                {viewType === 'my-day' ? (
                  <Sun className="h-12 w-12 mx-auto mb-4 opacity-50" />
                ) : viewType === 'important' ? (
                  <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
                ) : viewType === 'mentions' ? (
                  <AtSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                ) : (
                  <Check className="h-12 w-12 mx-auto mb-4 opacity-50" />
                )}
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {viewType === 'my-day'
                  ? "Rien de prévu pour aujourd'hui"
                  : viewType === 'important'
                  ? "Aucune tâche importante"
                  : viewType === 'mentions'
                  ? "Vous n'êtes mentionné dans aucune tâche"
                  : "Aucune tâche"}
              </p>
            </div>
          ) : viewType === 'planned' && groupedTasks ? (
            // Grouped view for planned
            groupedTasks.map(([dateKey, dateTasks]) => (
              <div key={dateKey} className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                  {formatDateHeader(dateKey)}
                </h3>
                {dateTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onToggleComplete={toggleComplete}
                    onToggleImportant={toggleImportant}
                    onToggleMyDay={toggleMyDay}
                    onClick={() => openTaskDetail(task)}
                    formatDueDate={formatDueDate}
                    getDueDateColor={getDueDateColor}
                  />
                ))}
              </div>
            ))
          ) : (
            // Regular list view
            tasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                onToggleComplete={toggleComplete}
                onToggleImportant={toggleImportant}
                onToggleMyDay={toggleMyDay}
                onClick={() => openTaskDetail(task)}
                formatDueDate={formatDueDate}
                getDueDateColor={getDueDateColor}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={showTaskDetail}
        onClose={() => { setShowTaskDetail(false); setSelectedTask(null); }}
        onUpdate={() => { fetchTasks(); refreshData?.(); }}
        onDelete={deleteTask}
        lists={lists}
      />
    </div>
  );
}

function TaskItem({ task, onToggleComplete, onToggleImportant, onToggleMyDay, onClick, formatDueDate, getDueDateColor }) {
  const isCompleted = task.status === 'done';
  const priority = PRIORITIES.find(p => p.value === task.priority);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const token = localStorage.getItem('token');
  
  // Fetch assigned users info
  useEffect(() => {
    const fetchAssignedUsers = async () => {
      if (!task.assigned_to || task.assigned_to.length === 0) return;
      
      try {
        const users = await Promise.all(
          task.assigned_to.slice(0, 3).map(async (userId) => {
            const res = await fetch(`${API_URL}/api/tasks/team/users/${userId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
              return res.json();
            }
            return null;
          })
        );
        setAssignedUsers(users.filter(Boolean));
      } catch (err) {
        console.error('Error fetching assigned users:', err);
      }
    };
    
    fetchAssignedUsers();
  }, [task.assigned_to, token]);
  
  return (
    <div
      onClick={onClick}
      className={cn(
        "group flex items-center gap-3 p-3 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-all mb-1",
        isCompleted && "opacity-60"
      )}
      data-testid={`task-item-${task.id}`}
    >
      {/* Checkbox */}
      <div onClick={(e) => onToggleComplete(task, e)} className="flex-shrink-0">
        <div className={cn(
          "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer",
          isCompleted 
            ? "bg-blue-500 border-blue-500" 
            : "border-gray-300 dark:border-gray-600 hover:border-blue-500"
        )}>
          {isCompleted && <Check className="h-3 w-3 text-white" />}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm font-medium truncate",
          isCompleted ? "line-through text-gray-400" : "text-gray-900 dark:text-white"
        )}>
          {task.title}
        </p>
        
        {/* Meta info */}
        <div className="flex items-center gap-2 mt-1">
          {task.due_date && (
            <span className={cn("text-xs flex items-center gap-1", getDueDateColor(task.due_date, task.status))}>
              <Calendar className="h-3 w-3" />
              {formatDueDate(task.due_date, task.due_time)}
            </span>
          )}
          {task.recurrence?.enabled && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Repeat className="h-3 w-3" />
            </span>
          )}
          {task.subtasks?.length > 0 && (
            <span className="text-xs text-gray-400">
              {task.subtasks.filter(s => s.status === 'done').length}/{task.subtasks.length}
            </span>
          )}
          {priority && priority.value !== 'normal' && (
            <span style={{ color: priority.color }} className="text-xs">
              {priority.icon}
            </span>
          )}
          {/* Assigned users avatars */}
          {assignedUsers.length > 0 && (
            <UserAvatarGroup users={assignedUsers} max={2} size="xs" />
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => onToggleMyDay(task, e)}
          className={cn(
            "p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700",
            task.is_in_my_day ? "text-yellow-500" : "text-gray-400"
          )}
          title="Ajouter à Ma journée"
        >
          <Sun className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => onToggleImportant(task, e)}
          className={cn(
            "p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700",
            task.is_important ? "text-red-500" : "text-gray-400"
          )}
          title="Marquer comme important"
        >
          <Star className="h-4 w-4" fill={task.is_important ? "currentColor" : "none"} />
        </button>
      </div>
    </div>
  );
}

function TaskDetailSheet({ task, open, onClose, onUpdate, onDelete, lists }) {
  const [editedTask, setEditedTask] = useState(task || {});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [commentMentions, setCommentMentions] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null); // For threaded replies
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (task) {
      setEditedTask(task);
      fetchComments();
      fetchActivity();
      fetchAssignedUsers();
    }
  }, [task]);

  const fetchAssignedUsers = async () => {
    if (!task?.assigned_to || task.assigned_to.length === 0) {
      setAssignedUsers([]);
      return;
    }
    
    try {
      const users = await Promise.all(
        task.assigned_to.map(async (userId) => {
          const res = await fetch(`${API_URL}/api/tasks/team/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            return res.json();
          }
          return null;
        })
      );
      setAssignedUsers(users.filter(Boolean));
    } catch (err) {
      console.error('Error fetching assigned users:', err);
    }
  };

  const fetchComments = async () => {
    if (!task?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setComments(data.comments || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const fetchActivity = async () => {
    if (!task?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setActivity(data.activity || []);
    } catch (err) {
      console.error('Error fetching activity:', err);
    }
  };

  const updateTask = async (updates) => {
    if (!task?.id) return;
    try {
      await fetch(`${API_URL}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      setEditedTask(prev => ({ ...prev, ...updates }));
      onUpdate();
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  const updateAssignees = async (users) => {
    setAssignedUsers(users);
    const userIds = users.map(u => u.id);
    await updateTask({ assigned_to: userIds });
  };

  const addComment = async () => {
    if (!newComment.trim() || !task?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: newComment, 
          mentions: commentMentions,
          parent_comment_id: replyingTo?.id || null
        })
      });
      const data = await res.json();
      
      // If users were auto-assigned via mention, refresh the assignees
      if (data.auto_assigned && data.auto_assigned.length > 0) {
        fetchAssignedUsers();
        onUpdate(); // Refresh task list to show new assignees
      }
      
      setNewComment('');
      setCommentMentions([]);
      setReplyingTo(null);
      fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  if (!task || !editedTask) return null;

  const priority = PRIORITIES.find(p => p.value === editedTask?.priority);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg">{editedTask.title}</SheetTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onDelete(task.id)} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex border-b mt-4">
          {['details', 'comments', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "flex-1 py-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === tab
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              {tab === 'details' && 'Détails'}
              {tab === 'comments' && `Commentaires (${comments.length})`}
              {tab === 'activity' && 'Activité'}
            </button>
          ))}
        </div>

        <div className="py-4 space-y-4">
          {activeTab === 'details' && (
            <>
              {/* Title */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Titre</label>
                <Input
                  value={editedTask.title}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, title: e.target.value }))}
                  onBlur={() => updateTask({ title: editedTask.title })}
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                <Textarea
                  value={editedTask.description || ''}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={() => updateTask({ description: editedTask.description })}
                  placeholder="Ajouter une description..."
                  rows={3}
                />
              </div>

              {/* Due Date & Time */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date et heure d'échéance</label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start">
                        <Calendar className="h-4 w-4 mr-2" />
                        {editedTask.due_date 
                          ? format(parseISO(editedTask.due_date.slice(0, 10)), 'dd MMM yyyy', { locale: fr })
                          : 'Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={editedTask.due_date ? parseISO(editedTask.due_date.slice(0, 10)) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            updateTask({ due_date: dateStr });
                          } else {
                            updateTask({ due_date: null, due_time: null });
                          }
                        }}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="time"
                      className="pl-9 pr-3 py-2 border rounded-md text-sm w-28 bg-white dark:bg-gray-800"
                      value={editedTask.due_time || ''}
                      onChange={(e) => {
                        updateTask({ due_time: e.target.value || null });
                      }}
                      disabled={!editedTask.due_date}
                      placeholder="--:--"
                    />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Priorité</label>
                <Select
                  value={editedTask.priority}
                  onValueChange={(value) => updateTask({ priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-2">
                          <span>{p.icon}</span>
                          {p.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* List */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Liste</label>
                <Select
                  value={editedTask.list_id || 'none'}
                  onValueChange={(value) => updateTask({ list_id: value === 'none' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucune liste" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune liste</SelectItem>
                    {lists?.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        <span className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: list.color }}
                          />
                          {list.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Assignation */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Assigner à
                </label>
                <UserAssignSelect
                  selectedUsers={assignedUsers}
                  onChange={updateAssignees}
                  placeholder="Sélectionner des utilisateurs..."
                />
              </div>

              {/* Quick toggles */}
              <div className="flex gap-2">
                <Button
                  variant={editedTask.is_important ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateTask({ is_important: !editedTask.is_important })}
                >
                  <Star className="h-4 w-4 mr-1" fill={editedTask.is_important ? "currentColor" : "none"} />
                  Important
                </Button>
                <Button
                  variant={editedTask.is_in_my_day ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateTask({ is_in_my_day: !editedTask.is_in_my_day })}
                >
                  <Sun className="h-4 w-4 mr-1" />
                  Ma journée
                </Button>
              </div>
            </>
          )}

          {activeTab === 'comments' && (
            <>
              <div className="space-y-3">
                {comments.map((comment) => (
                  <CommentItem 
                    key={comment.id} 
                    comment={comment} 
                    onReply={(c) => setReplyingTo(c)}
                    depth={0}
                  />
                ))}
                {comments.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun commentaire</p>
                )}
              </div>
              
              <div className="mt-4">
                {replyingTo && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      Réponse à {replyingTo.user_name}
                    </span>
                    <button 
                      onClick={() => setReplyingTo(null)}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-400 mb-2">Tapez @ pour mentionner un utilisateur</p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <UserMentionInput
                      value={newComment}
                      onChange={setNewComment}
                      placeholder={replyingTo ? `Répondre à ${replyingTo.user_name}...` : "Ajouter un commentaire... (@nom pour mentionner)"}
                      onMentionsChange={setCommentMentions}
                    />
                  </div>
                  <Button onClick={addComment} size="icon">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              {activity.map((log) => (
                <div key={log.id} className="flex items-start gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                  <div>
                    <span className="font-medium">{log.user_name || 'Utilisateur'}</span>
                    <span className="text-gray-500"> {log.action}</span>
                    <div className="text-xs text-gray-400">
                      {format(parseISO(log.created_at), 'dd MMM HH:mm', { locale: fr })}
                    </div>
                  </div>
                </div>
              ))}
              {activity.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">Aucune activité</p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Comment with optional nested replies (non-recursive for build compatibility)
function CommentItem({ comment, onReply, depth = 0 }) {
  const maxDepth = 3;
  
  const renderComment = (c, d) => (
    <div key={c.id} className={cn(d > 0 && "ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-3")}>
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{c.user_name || 'Utilisateur'}</span>
            <span className="text-xs text-gray-500">
              {format(parseISO(c.created_at), 'dd MMM HH:mm', { locale: fr })}
            </span>
          </div>
          {d < maxDepth && (
            <button
              onClick={() => onReply(c)}
              className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1"
            >
              <MessageSquare className="h-3 w-3" />
              Répondre
            </button>
          )}
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300">{c.content}</p>
      </div>
      {c.replies && c.replies.length > 0 && (
        <div className="mt-2 space-y-2">
          {c.replies.map((reply) => renderComment(reply, d + 1))}
        </div>
      )}
    </div>
  );
  
  return renderComment(comment, depth);
}
